import type { CSSProperties } from 'react';
import { useTheme } from '@/contexts/ThemeContext';
import { getContrastHex } from '@/lib/colorUtils';

export interface Layout7ThemeColors {
  header: string;
  menu: string;
  primary: string;
  secondary: string;
  button: string;
  title: string;
  text: string;
}

/**
 * Cores do config — marca 100% do cliente (igual layouts 3–6):
 * - header  → fundo da barra principal do header
 * - menu    → topbar, links/ícones do menu, botão de serviço; item ativo = pill invertido
 * - primary → footer, faixas de seção (.wl7-band), blocos CTA
 * - secondary → badges secundários
 * - button  → CTAs, preços, faixa de busca, accent geral
 * - title   → títulos de seção (sobre fundo claro)
 * - text    → textos de corpo (sobre fundo claro)
 *
 * Fundos estruturais (--wl7-bg, --wl7-surface, etc.) ficam neutros em styles.css.
 */
export function getLayout7ThemeStyle(colors: Layout7ThemeColors): CSSProperties {
  return {
    '--wl7-accent': colors.button,
    '--l7-accent': colors.button,
    '--wl7-primary': colors.primary,
    '--wl7-secondary': colors.secondary,
    '--wl7-button': colors.button,
    '--wl7-title': colors.title,
    '--wl7-text': colors.text,
    '--wl7-header': colors.header,
    '--wl7-menu': colors.menu,
    '--wl7-on-primary': getContrastHex(colors.primary),
    '--wl7-on-button': getContrastHex(colors.button),
    '--wl7-on-header': getContrastHex(colors.header),
    '--wl7-on-menu': getContrastHex(colors.menu),
    '--wl7-accent-bg': `${colors.button}12`,
    '--wl7-primary-bg': `${colors.primary}12`,
    '--wl7-band': colors.primary,
    '--wl7-band-fg': getContrastHex(colors.primary),
  } as CSSProperties;
}

export const WL7_THEME_KEYS = [
  '--wl7-accent',
  '--l7-accent',
  '--wl7-primary',
  '--wl7-secondary',
  '--wl7-button',
  '--wl7-title',
  '--wl7-text',
  '--wl7-header',
  '--wl7-menu',
  '--wl7-on-primary',
  '--wl7-on-button',
  '--wl7-on-header',
  '--wl7-on-menu',
  '--wl7-accent-bg',
  '--wl7-primary-bg',
  '--wl7-band',
  '--wl7-band-fg',
] as const;

export function useLayout7Theme() {
  const { colors } = useTheme();

  return {
    colors,
    themeStyle: getLayout7ThemeStyle(colors),
    headerColor: colors.header,
    menuColor: colors.menu,
    primaryColor: colors.primary,
    secondaryColor: colors.secondary,
    buttonColor: colors.button,
    titleColor: colors.title,
    textColor: colors.text,
  };
}
